import { Link, useLocation } from "react-router-dom";
import { Gift } from "lucide-react";
import { Button } from "./ui/button";
import { CartDrawer } from "./CartDrawer";
import { ShakatahCartDrawer } from "./ShakatahCartDrawer";
import { useCartStore } from "@/stores/cartStore";
import { useShakatahCartStore } from "@/stores/shakatahCartStore";
import { useAbandonedCartSync } from "@/hooks/useAbandonedCartSync";
import { useMemo } from "react";

const SESSION_KEY = "shakatah_customer_session";

// Pages where cart drawers should be visible
const CART_ENABLED_ROUTES = [
  "/shakatah-checkout",
  "/shakatah-split",
  "/product/",
];

// Customer-focused routes (My Gifts, split tracking)
const CUSTOMER_ROUTES = [
  "/my-purchases",
  "/my-activity",
  "/split/",
  "/events/",
  "/purchases/",
];

// Check if user has a valid customer session
const hasCustomerSession = (): boolean => {
  try {
    const storedSession = sessionStorage.getItem(SESSION_KEY);
    if (storedSession) {
      const session = JSON.parse(storedSession);
      return !!session.email;
    }
  } catch {
    // Invalid session
  }
  return false;
};

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function Header() {
  const location = useLocation();
  
  // Check if current route should show cart drawers
  const showCarts = CART_ENABLED_ROUTES.some(route => 
    location.pathname.startsWith(route)
  );
  
  // Determine if we're in customer context
  const isCustomerContext = CUSTOMER_ROUTES.some(route => 
    location.pathname.startsWith(route)
  );
  
  // Determine home link based on session
  const homeLink = hasCustomerSession() ? "/my-purchases" : "/";
  
  // Sync split cart to database for abandoned cart reminders (logged-in users only)
  useAbandonedCartSync();
  
  // Use shallow selectors to prevent unnecessary re-renders
  const regularCartItems = useCartStore((state) => state.items);
  const splitCartItems = useShakatahCartStore((state) => state.items);
  
  // Memoize counts to prevent recalculation on every render
  const regularCartCount = useMemo(
    () => regularCartItems.reduce((sum, item) => sum + item.quantity, 0),
    [regularCartItems]
  );
  const splitCartCount = useMemo(
    () => splitCartItems.reduce((sum, item) => sum + item.quantity, 0),
    [splitCartItems]
  );

  return (
    <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
      <div className="container flex h-16 items-center justify-between">
        <Link to={homeLink} className="flex items-center gap-2 font-bold text-xl text-foreground tracking-tight">
          <Gift className="w-6 h-6 text-primary" />
          Shakatah
        </Link>
        
        <nav className="hidden md:flex items-center gap-8">
          {isCustomerContext ? (
            // Customer-focused navigation
            <>
              <Link to="/my-purchases" className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                My Purchases
              </Link>
            </>
          ) : (
            // Merchant-focused navigation (default)
            <>
              <Link to={homeLink} className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                Home
              </Link>
              <a href="/#how-it-works" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                How It Works
              </a>
              <Link to="/pricing" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Pricing
              </Link>
            </>
          )}
        </nav>

        <div className="flex items-center gap-3">
          {/* Show cart drawers only on checkout-related pages */}
          {showCarts && (
            <>
              <ShakatahCartDrawer />
              <CartDrawer />
            </>
          )}
          
          {isCustomerContext ? (
            <Button asChild variant="outline" size="sm">
              <Link to="/login?type=business">Business Login</Link>
            </Button>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm">
                <Link to="/my-purchases">Track Purchase</Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="/login">Log In</Link>
              </Button>
              <Button asChild size="sm">
                <Link to="/login?tab=signup">Sign Up Free</Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
